import React from "react";
import useProducts from "../../../components/admin/employees/hooks/useProducts";
import { SSOButton, AuthMode } from "../../../components/auth/SSOButton";
import { useFeatureFlag } from "../../../hooks/use-feature-flag";
import { OAuthIDP } from "../../../hooks/useOAuth";
import { useMedia } from "react-media";

interface UsePublicSSOButtonsProps {
  initiateOAuth: (idp: OAuthIDP) => void;
  isLoading?: boolean;
}

export const usePublicSSOButtons = ({ initiateOAuth, isLoading }: UsePublicSSOButtonsProps) => {
  const { hasSSOMicrosoft } = useProducts();
  const { isEnabled: hasSSOGoogle } = useFeatureFlag("sso.google");
  const { isEnabled: hasSSOFacebook } = useFeatureFlag("sso.facebook");
  const { isEnabled: hasSSOApple } = useFeatureFlag("sso.apple");

  const hasAnySSO = hasSSOMicrosoft || hasSSOGoogle || hasSSOFacebook || hasSSOApple;

  const isTinyScreen = useMedia({ query: "(max-width: 300px)" });

  const renderPublicSSOButtons = (): React.ReactNode => {
    const ssoButtons = [];

    if (hasSSOMicrosoft) {
      ssoButtons.push(
        <SSOButton
          key='microsoft'
          mode={AuthMode.Public}
          idp={OAuthIDP.Microsoft}
          initiateOAuth={initiateOAuth}
          isLoading={isLoading}
        />,
      );
    }
    if (hasSSOGoogle) {
      ssoButtons.push(
        <SSOButton
          key='google'
          mode={AuthMode.Public}
          idp={OAuthIDP.Google}
          initiateOAuth={initiateOAuth}
          isLoading={isLoading}
        />,
      );
    }
    if (hasSSOApple) {
      ssoButtons.push(
        <SSOButton
          key='apple'
          mode={AuthMode.Public}
          idp={OAuthIDP.Apple}
          initiateOAuth={initiateOAuth}
          isLoading={isLoading}
        />,
      );
    }
    if (hasSSOFacebook) {
      ssoButtons.push(
        <SSOButton
          key='facebook'
          mode={AuthMode.Public}
          idp={OAuthIDP.Facebook}
          initiateOAuth={initiateOAuth}
          isLoading={isLoading}
        />,
      );
    }

    // Stack buttons on tiny screens
    if (isTinyScreen) {
      return <div className='tw-flex tw-flex-col tw-gap-y-0'>{ssoButtons}</div>;
    }

    const buttonCount = ssoButtons.length;

    // No buttons
    if (buttonCount === 0) {
      return null;
    }

    // Single button - center it like employee mode
    if (buttonCount === 1) {
      return (
        <div className='tw-flex tw-flex-col tw-items-center tw-justify-center'>
          <div className='tw-w-1/2'>{ssoButtons[0]}</div>
        </div>
      );
    }

    // Two/Four buttons - use existing grid layout
    if (buttonCount > 0 && buttonCount % 2 === 0) {
      return <div className='tw-grid tw-grid-cols-2 tw-gap-x-2'>{ssoButtons}</div>;
    }

    // Three buttons - first two in grid, third centered below
    if (buttonCount === 3) {
      return (
        <div>
          <div className='tw-grid tw-grid-cols-2 tw-gap-x-2'>{ssoButtons.slice(0, 2)}</div>
          <div className='tw-flex tw-justify-center'>
            <div className='tw-w-1/2'>{ssoButtons[2]}</div>
          </div>
        </div>
      );
    }

    // Fallback for any other count
    return <div className='tw-grid tw-grid-cols-2 tw-gap-x-2'>{ssoButtons}</div>;
  };

  return {
    hasSSOMicrosoft,
    hasSSOGoogle,
    hasSSOFacebook,
    hasSSOApple,
    hasAnySSO,
    renderPublicSSOButtons,
  };
};
